import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card";
import {Progress} from "@/components/ui/progress";
import {cn} from "@/lib/utils";

import type {DashboardBreakdownRow} from "../types";

type BreakdownBarListProps = {
  title: string;
  rows: DashboardBreakdownRow[];
  emptyText?: string;
  /** Cap rendered rows; defaults to 6 (top-N). */
  limit?: number;
};

/**
 * Generic top-N breakdown card. Each row shows a label + count and a
 * proportional bar relative to the largest count in the set.
 *
 */
export function BreakdownBarList({
  title,
  rows,
  emptyText = "No data for this period.",
  limit = 6,
}: BreakdownBarListProps) {
  const visible = rows.slice(0, limit);
  const max = visible.reduce((acc, r) => Math.max(acc, r.count), 0);

  return (
    <Card className="gap-3 py-4">
      <CardHeader className="gap-2">
        <CardTitle className="text-base font-bold tracking-tight">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {visible.length === 0 ? (
          <p className="text-muted-foreground text-sm">{emptyText}</p>
        ) : (
          <ul className="flex flex-col gap-2.5">
            {visible.map((row, i) => {
              const pct = max > 0 ? (row.count / max) * 100 : 0;
              return (
                <li key={`${row.key}-${i}`} className="flex flex-col gap-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <span
                      className={cn(
                        "text-foreground text-sm font-semibold",
                        "min-w-0 truncate",
                      )}
                    >
                      {row.label || row.key}
                    </span>
                    <span className="text-muted-foreground tabular-nums text-xs">
                      {row.count.toLocaleString()}
                    </span>
                  </div>
                  <Progress value={pct} className="h-1.5" />
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
